import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listConsumables, createConsumable, toggleConsumable } from "@/lib/admin-config.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/admin/consumables")({ component: ConsumablesPage });

const CATEGORIES = ["box", "mailer", "pallet", "dunnage", "label", "other"] as const;
type Category = (typeof CATEGORIES)[number];

function ConsumablesPage() {
  const qc = useQueryClient();
  const { tenantId } = useScope();

  const fetchRows = useServerFn(listConsumables);
  const createFn = useServerFn(createConsumable);
  const toggleFn = useServerFn(toggleConsumable);

  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState<Category>("box");
  const [costDollars, setCostDollars] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { data: rows, isLoading } = useQuery({
    queryKey: ["consumables", tenantId],
    enabled: !!tenantId,
    queryFn: () => runServerFn(() => fetchRows({ data: { tenantId: tenantId! } }), "Loading consumables"),
  });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantId || !code.trim() || !name.trim()) return;
    setSubmitting(true);
    const cents = Math.round(Number(costDollars || "0") * 100);
    try {
      await runServerFn(() => createFn({ data: {
        tenantId, code: code.trim(), name: name.trim(),
        category, unitCostCents: Number.isFinite(cents) ? cents : 0,
      } }), "Adding consumable");
      toast.success("Consumable added");
      setOpen(false); setCode(""); setName(""); setCategory("box"); setCostDollars("");
      qc.invalidateQueries({ queryKey: ["consumables", tenantId] });
    } catch { /* */ } finally { setSubmitting(false); }
  };

  const toggleActive = async (id: string, active: boolean) => {
    try {
      await runServerFn(() => toggleFn({ data: { id, active: !active } }), "Updating consumable");
      qc.invalidateQueries({ queryKey: ["consumables", tenantId] });
    } catch { /* */ }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Consumables</h2>
          <p className="text-sm text-muted-foreground">Boxes, mailers, and packing materials.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button disabled={!tenantId}><Plus className="h-4 w-4 mr-2" />New consumable</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add consumable</DialogTitle></DialogHeader>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Code</Label><Input required value={code} onChange={(e) => setCode(e.target.value)} placeholder="BX-12X10" /></div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={category} onValueChange={(v) => setCategory(v as Category)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2"><Label>Name</Label><Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="12x10x6 corrugated box" /></div>
              <div className="space-y-2"><Label>Unit cost (USD)</Label><Input type="number" step="0.01" min="0" value={costDollars} onChange={(e) => setCostDollars(e.target.value)} /></div>
              <DialogFooter><Button type="submit" disabled={submitting}>{submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Add</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border rounded-lg bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Category</TableHead>
            <TableHead className="text-right">Unit cost</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : rows && rows.length > 0 ? rows.map((r: any) => (
              <TableRow key={r.id}>
                <TableCell className="font-mono text-sm">{r.code}</TableCell>
                <TableCell>{r.name}</TableCell>
                <TableCell className="text-muted-foreground">{r.category}</TableCell>
                <TableCell className="text-right tabular-nums">${(r.unit_cost_cents / 100).toFixed(2)}</TableCell>
                <TableCell className="text-muted-foreground">{r.active ? "active" : "archived"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => toggleActive(r.id, r.active)}>
                    {r.active ? "Archive" : "Restore"}
                  </Button>
                </TableCell>
              </TableRow>
            )) : (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No consumables yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
